// SHERIK AUTENTIFIKATSIYASI — ADR-029 §4.
//
// ⚠️ BU KONTRAKT TAXMIN EMAS, BACKENDDAN O'QIB OLINGAN:
// `xsib-identity` → `auth/resource/PartnerAuthResource.java` va
// `auth/resource/LoginStepPresenter.java`.
//
//   POST /api/partner/login        {username, password}
//     → 200 {status:"TOTP_SETUP", setupToken, qrUri, secret}   — birinchi marta
//     → 200 {status:"TOTP_REQUIRED", totpToken}                — har safar
//   POST /api/partner/totp/confirm {setupToken, code}
//   POST /api/partner/totp/verify  {totpToken, code}
//     → 200 {status:"CHOOSE_TENANT", choiceToken, candidates:[...]}
//     → 200 {token, expiresAt, refreshToken, refreshExpiresAt, user}
//   POST /api/partner/login/tenant {choiceToken, tenantId} → yakuniy shakl
//
// Panel uchun qoida `LoginStepPresenter` javadoc'ida yozilgan va u
// SHU YERDA ham qo'llanadi: **`token` bo'lsa kirish tugadi**, aks holda
// `status` ga qarab keyingi bosqich chiziladi.
//
// ⚠️ TOTP MAJBURIY — kassir uchun ham. Ilgari bu ekran TOTP'siz yozilgan
// edi (kontrakt hali yo'q edi, taxmin qilib yozish noto'g'ri bo'lardi);
// backend chiqqach ma'lum bo'ldiki, sherik xodim bilan AYNAN bir xil
// bosqichlardan o'tadi — `LoginFlowService` bitta, farq faqat
// `UserKind.PARTNER` parametrida.
//
// ⚠️ `refresh` VA `logout` UMUMIY YO'LDA (`/api/auth/*`) va bu ataylab:
// ular kirish yo'li emas va sinfni hisobning O'ZIDAN o'qiydi
// (`PartnerAuthResource` javadoc'i). Ularni `/api/partner/*` ga
// ko'chirish ikki yo'lni vaqt o'tib ajratib yuborardi.

import axios from 'axios'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { getRefreshToken } from './tokenStorage'

export const PARTNER_LOGIN_PATH = '/api/partner/login'
export const PARTNER_TOTP_CONFIRM_PATH = '/api/partner/totp/confirm'
export const PARTNER_TOTP_VERIFY_PATH = '/api/partner/totp/verify'
export const PARTNER_TENANT_PATH = '/api/partner/login/tenant'

/** Sherik va xodim uchun BIR XIL (`AuthResource#me`, `#logout`) */
export const ME_PATH = '/api/me'
export const LOGOUT_PATH = '/api/auth/logout'

const scopeSchema = z.object({
  type: z.string(),
  /** `SUBTREE`/`OWN` uchun nishon; `TENANT` da umuman kelmaydi */
  scopeId: z.string().nullish(),
})

/**
 * ⚠️ HUQUQ KODLARI FILTRLANMAYDI — admin panelidan ATAYLAB farqli.
 *
 * U yerda `authApi.toCurrentUser` katalogda (`shared/auth/permissions.ts`)
 * bo'lmagan kodni JIMGINA tashlab yuboradi va o'sha nuqson besh marta
 * takrorlangan: huquqi BOR operator uchun ham tugma ko'rinmasdi, hech
 * qanday xato chiqmasdi. Bu yerda katalog UMUMAN YO'Q — server yuborgan
 * har qanday kod shundayligicha to'plamga tushadi.
 *
 * Sherik uchun bu xavfsiz: uning to'plami KODDA qotirilgan (ADR-029 §2),
 * ya'ni "noma'lum kod" holati ro'l tahriridan emas, faqat versiya
 * farqidan kelib chiqadi.
 */
const meSchema = z.object({
  userId: z.string(),
  tenantId: z.string(),
  username: z.string(),
  tenant: z
    .object({
      name: z.string().nullish(),
      currency: z.string().nullish(),
      dayTimezone: z.string().nullish(),
    })
    .nullish(),
  permissions: z.array(z.string()).default([]),
  scopes: z.array(scopeSchema).default([]),
  /** `OWN` doirasining nishoni — «men qaysi agentman». RESELLER da `null`. */
  partyNodeId: z.string().nullish(),
})

export type MeResponseRaw = z.infer<typeof meSchema>

export interface CurrentUser {
  id: string
  tenantId: string
  username: string
  tenantName: string
  currency: string | null
  dayTimezone: string | null
  permissions: ReadonlySet<string>
  scopeTypes: ReadonlySet<string>
  partyNodeId: string | null
}

function toCurrentUser(raw: MeResponseRaw): CurrentUser {
  return {
    id: raw.userId,
    tenantId: raw.tenantId,
    username: raw.username,
    tenantName: raw.tenant?.name ?? raw.tenantId,
    currency: raw.tenant?.currency ?? null,
    dayTimezone: raw.tenant?.dayTimezone ?? null,
    permissions: new Set(raw.permissions),
    scopeTypes: new Set(raw.scopes.map((s) => s.type)),
    partyNodeId: raw.partyNodeId ?? null,
  }
}

const completedSchema = z.object({
  token: z.string(),
  expiresAt: z.string().nullish(),
  /**
   * ⚠️ `nullish` ATAYLAB: majburiy qilinsa, uni bermaydigan backend
   * versiyasida `parse` yiqiladi va odam UMUMAN kira olmaydi. Kelmasa
   * sessiya 30 daqiqada tugaydi — "kira olmaslik" dan ancha yengil.
   */
  refreshToken: z.string().nullish(),
  user: meSchema,
})

export interface LoginSuccess {
  token: string
  expiresAt: string | null
  refreshToken: string | null
  user: CurrentUser
}

function toLoginSuccess(parsed: z.infer<typeof completedSchema>): LoginSuccess {
  return {
    token: parsed.token,
    expiresAt: parsed.expiresAt ?? null,
    refreshToken: parsed.refreshToken ?? null,
    user: toCurrentUser(parsed.user),
  }
}

/** `LoginStep.TenantCandidate` — maydon nomi `tenantId`, `id` EMAS */
const tenantCandidateSchema = z.object({
  tenantId: z.string(),
  code: z.string(),
  name: z.string(),
})

export interface TenantCandidate {
  tenantId: string
  code: string
  name: string
}

const chooseTenantSchema = z.object({
  status: z.literal('CHOOSE_TENANT'),
  choiceToken: z.string(),
  candidates: z.array(tenantCandidateSchema),
})

const totpSetupSchema = z.object({
  status: z.literal('TOTP_SETUP'),
  setupToken: z.string(),
  qrUri: z.string(),
  secret: z.string(),
})

const totpRequiredSchema = z.object({
  status: z.literal('TOTP_REQUIRED'),
  totpToken: z.string(),
})

/**
 * `status` maydoniga qarab javob shaklini aniqlaydi.
 *
 * ⚠️ Yakuniy javobda `status` YO'Q (`LoginStepPresenter`) — shuning uchun
 * u `.optional()` va uning yo'qligi "kirish tugadi" degani.
 */
const statusPeekSchema = z
  .object({
    status: z.enum(['CHOOSE_TENANT', 'TOTP_SETUP', 'TOTP_REQUIRED']).optional(),
  })
  .passthrough()

/** Login HAR DOIM shu ikkitadan biriga olib keladi — TOTP majburiy */
export type LoginOutcome =
  | { kind: 'totpSetup'; setupToken: string; qrUri: string; secret: string }
  | { kind: 'totpRequired'; totpToken: string }

export interface LoginRequest {
  username: string
  password: string
}

export async function login(request: LoginRequest): Promise<LoginOutcome> {
  const response = await api.post<unknown>(PARTNER_LOGIN_PATH, request)
  const peek = statusPeekSchema.parse(response.data)

  if (peek.status === 'TOTP_SETUP') {
    const parsed = totpSetupSchema.parse(response.data)
    return {
      kind: 'totpSetup',
      setupToken: parsed.setupToken,
      qrUri: parsed.qrUri,
      secret: parsed.secret,
    }
  }
  if (peek.status === 'TOTP_REQUIRED') {
    const parsed = totpRequiredSchema.parse(response.data)
    return { kind: 'totpRequired', totpToken: parsed.totpToken }
  }

  // TOTP tenantdan mustaqil va MAJBURIY, ya'ni login boshqa shaklda
  // javob BERMASLIGI kerak. Kelib qolsa — kontraktdagi kutilmagan
  // o'zgarish, chegarada ushlanadi.
  throw new Error(`partner login: kutilmagan javob shakli — ${JSON.stringify(response.data)}`)
}

/**
 * TOTP natijasi UCH XIL bo'lishi mumkin:
 *   · `authenticated` — bitta a'zolik, kirish shu yerda tugaydi
 *   · `chooseTenant`  — bir nechta a'zolik
 *   · `noMembership`  — hisobning FAOL a'zoligi yo'q (backend 403)
 */
export type TotpOutcome =
  | ({ kind: 'authenticated' } & LoginSuccess)
  | { kind: 'chooseTenant'; choiceToken: string; candidates: TenantCandidate[] }
  | { kind: 'noMembership' }

async function postTotp(url: string, body: unknown): Promise<TotpOutcome> {
  let data: unknown
  try {
    const response = await api.post<unknown>(url, body)
    data = response.data
  } catch (error) {
    // 403 — hisobning HECH QANDAY faol a'zoligi yo'q. Bu boshqa
    // 403'lardan (huquq yetmasligi) farqli holat: bu yerda sessiya
    // hali UMUMAN yo'q, shuning uchun oddiy xato emas, aniq natija.
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      return { kind: 'noMembership' }
    }
    throw error
  }

  const peek = statusPeekSchema.parse(data)
  if (peek.status === 'CHOOSE_TENANT') {
    const parsed = chooseTenantSchema.parse(data)
    return { kind: 'chooseTenant', choiceToken: parsed.choiceToken, candidates: parsed.candidates }
  }
  return { kind: 'authenticated', ...toLoginSuccess(completedSchema.parse(data)) }
}

/** Birinchi TOTP sozlashda — QR skanerlangandan/sirni kiritgandan keyin */
export async function confirmTotp(setupToken: string, code: string): Promise<TotpOutcome> {
  return postTotp(PARTNER_TOTP_CONFIRM_PATH, { setupToken, code })
}

/** Har safargi kirishda — TOTP kod tekshiruvi */
export async function verifyTotp(totpToken: string, code: string): Promise<TotpOutcome> {
  return postTotp(PARTNER_TOTP_VERIFY_PATH, { totpToken, code })
}

/** `CHOOSE_TENANT` bosqichida tashkilot tanlanganda — YAKUNIY qadam */
export async function chooseTenant(choiceToken: string, tenantId: string): Promise<LoginSuccess> {
  const response = await api.post<unknown>(PARTNER_TENANT_PATH, { choiceToken, tenantId })
  return toLoginSuccess(completedSchema.parse(response.data))
}

export async function fetchMe(): Promise<CurrentUser> {
  const response = await api.get<unknown>(ME_PATH)
  return toCurrentUser(meSchema.parse(response.data))
}

/**
 * Chiqish — tokenni SERVER tomonda ham bekor qiladi.
 *
 * ⚠️ Mahalliy tozalash YETMAYDI: `AuthResource#logout` refresh tokenni
 * bekor qiladi VA joriy access tokenni qora ro'yxatga qo'shadi. Usiz
 * o'g'irlangan token 30 daqiqa ishlashda davom etardi va refresh
 * tokeni 14 KUN.
 *
 * ⚠️ Kassir uchun bu oddiy tozalik emas, TALAB: u umumiy telefondan
 * yoki do'kondagi kompyuterdan ishlaydi va uning tokeni ostida
 * BOSHQA ODAMNING depozitidan pul yechish mumkin.
 *
 * ⚠️ Backend `@NotBlank refreshToken` talab qiladi — qiymatsiz so'rov
 * 400 bo'lardi. Bekor qiladigan narsa yo'q, ya'ni chaqiruvning ma'nosi
 * ham yo'q.
 *
 * ⚠️ Xato YUTILADI: tarmoq uzilgan bo'lsa ham odam CHIQISHI kerak.
 */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken()
  if (refreshToken === null) return
  try {
    await api.post(LOGOUT_PATH, { refreshToken })
  } catch {
    // ataylab jim — yuqoridagi izoh
  }
}
